import React, { Component } from 'react';
import domRectContains from '~/libs/DOM/domRectContains';
import * as KeyCode from '~/libs/KeyCode';
import Button from '~/UI/Button';
import Icon from '~/UI/Icon/Icon';
import Portal from '~/UI/Portal/Portal';

import defaultStyles from './default.less';

export const Context = React.createContext({
	body: '',
	bodyIn: '',
	content: '',
	footer: '',
	header: '',
});

interface Props {
	children: React.ReactNode;
	styles: {
		body: string;
		bodyIn: string;
		closeButton: string;
		container: string;
		content: string;
		footer: string;
		header: string;
		nextArrowButton: string;
		overlay: string;
		previousArrowButton: string;
	};
	closeButtonIcon: React.ReactNode;
	onClose?: (() => void) | null;
	onPrevious?: (() => void) | null;
	onNext?: (() => void) | null;
}

export default class Modal extends Component<Props> {
	static defaultProps = {
		styles: defaultStyles,
		closeButtonIcon: <Icon name='modal-close'>close</Icon>,
	};

	containerElement: HTMLDivElement | null = null;

	previousButtonElement: HTMLButtonElement | null = null;

	nextButtonElement: HTMLButtonElement | null = null;

	componentDidMount() {
		window.addEventListener('keyup', this.handleKeyUp);
	}

	componentWillUnmount() {
		window.removeEventListener('keyup', this.handleKeyUp);
	}

	render() {
		const { children, styles, closeButtonIcon, onPrevious = null, onNext = null, onClose } = this.props;

		return (
			<Portal>
				<div className={styles.overlay} onClick={this.handlOverlayClick} />

				{onPrevious !== null ? (
					<Button
						buttonRef={(ref) => (this.previousButtonElement = ref)}
						styles={{ button: styles.previousArrowButton }}
						onClick={onPrevious}
					>
						<Icon name='modal-arrow-previous'>previous</Icon>
					</Button>
				) : null}

				{onNext !== null ? (
					<Button
						buttonRef={(ref) => (this.nextButtonElement = ref)}
						styles={{ button: styles.nextArrowButton }}
						onClick={onNext}
					>
						<Icon name='modal-arrow-next'>next</Icon>
					</Button>
				) : null}

				<div className={styles.container} data-testid='modal' ref={(ref) => (this.containerElement = ref)}>
					<Button styles={{ button: styles.closeButton }} onClick={() => onClose?.()}>
						{closeButtonIcon}
					</Button>
					<Context.Provider value={styles}>{children}</Context.Provider>
				</div>
			</Portal>
		);
	}

	handleKeyUp = (event: KeyboardEvent) => {
		const { onClose, onPrevious, onNext } = this.props;

		if (event.keyCode === KeyCode.ESCAPE) {
			onClose?.();
		} else if (event.keyCode === KeyCode.LEFT) {
			onPrevious?.();
		} else if (event.keyCode === KeyCode.RIGHT) {
			onNext?.();
		}
	};

	handlOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
		const { onClose } = this.props;

		if (
			!onClose ||
			this.isClickOn(event, this.containerElement) ||
			this.isClickOn(event, this.previousButtonElement) ||
			this.isClickOn(event, this.nextButtonElement)
		) {
			return;
		}

		event.stopPropagation();
		onClose();
	};

	isClickOn(event: React.MouseEvent<HTMLDivElement>, element: HTMLElement | null): boolean {
		return (
			element !== null &&
			(element === event.target ||
				(event.target instanceof HTMLElement && element.contains(event.target)) ||
				domRectContains(element.getBoundingClientRect(), event.clientX, event.clientY))
		);
	}
}
